import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import { PageHeader } from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { FAQ_ITEMS } from "@/data/faq";

const FaqItem = ({ item, index, open, onToggle }) => (
  <div className="border-b border-white/10" data-testid={`faq-item-${index}`}>
    <button
      onClick={onToggle}
      aria-expanded={open}
      aria-controls={`faq-panel-${index}`}
      data-testid={`faq-question-${index}`}
      className="group flex w-full items-center justify-between gap-6 py-6 text-left"
    >
      <span className="font-display text-base font-semibold text-paper-100 transition-colors duration-300 group-hover:text-white md:text-lg">
        {item.q}
      </span>
      <motion.span
        animate={{ rotate: open ? 180 : 0 }}
        transition={{ duration: 0.3 }}
        className="shrink-0 rounded-full border border-white/15 p-2 text-paper-400 transition-colors duration-300 group-hover:border-white/40 group-hover:text-paper-100"
      >
        <ChevronDown className="h-4 w-4" aria-hidden="true" />
      </motion.span>
    </button>
    <AnimatePresence initial={false}>
      {open && (
        <motion.div
          id={`faq-panel-${index}`}
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="overflow-hidden"
        >
          <p className="max-w-3xl pb-6 text-sm leading-relaxed text-paper-400 md:text-base" data-testid={`faq-answer-${index}`}>
            {item.a}
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

export default function Faq() {
  useSEO({
    title: "FAQ",
    description: "Frequently asked questions about Xevon — setup, permissions, Premium, support, and data deletion.",
    path: "/faq",
  });

  const [openIndex, setOpenIndex] = useState(0);

  return (
    <>
      <PageHeader
        kicker="FAQ"
        title="Answers, before you ask."
        description="The most common questions about Xevon — from invites and permissions to Premium and data deletion."
      />
      <section className="mx-auto w-full max-w-4xl px-6 pb-32" data-testid="faq-section">
        <Reveal>
          <div className="border-t border-white/10">
            {FAQ_ITEMS.map((item, i) => (
              <FaqItem
                key={item.q}
                item={item}
                index={i}
                open={openIndex === i}
                onToggle={() => setOpenIndex(openIndex === i ? -1 : i)}
              />
            ))}
          </div>
        </Reveal>
      </section>
    </>
  );
}
